import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { CheckCircle2 } from 'lucide-react';
import {
  UniversalPage,
  Button,
  Input,
  Card,
  FormField,
  NotificationCard,
  Checkbox,
} from '@sighthost/ui';
import {
  adminCustomerService,
  type ImportExistingCustomerInput,
} from '@/domains/customers/services/adminCustomerService';

const PLAN_OPTIONS = [
  { slug: '', label: '— No plan yet (set later) —' },
  { slug: 'entry-monthly', label: 'SightHost Entry' },
  { slug: 'basic-monthly', label: 'SightHost Basic' },
  { slug: 'super-monthly', label: 'SightHost Super' },
  { slug: 'ultimate-monthly', label: 'SightHost Ultimate' },
];

const EMPTY: ImportExistingCustomerInput = {
  email: '',
  full_name: '',
  company: '',
  cpanel_username: '',
  whm_server: 'rs61.cphost.co.za',
  primary_domain: '',
  plan_slug: '',
  send_invite: true,
};

export default function ImportExistingCustomerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ImportExistingCustomerInput>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState<{
    auth_user_id: string;
    customer_link_id: string;
    invite_sent: boolean;
  } | null>(null);

  function setField<K extends keyof ImportExistingCustomerInput>(
    key: K,
    value: ImportExistingCustomerInput[K]
  ) {
    setData((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setImported(null);
    setSubmitting(true);

    const input: ImportExistingCustomerInput = {
      email: data.email.trim(),
      full_name: data.full_name.trim(),
      cpanel_username: data.cpanel_username.trim(),
      primary_domain: data.primary_domain.trim().toLowerCase(),
      whm_server: data.whm_server?.trim() || 'rs61.cphost.co.za',
      send_invite: data.send_invite,
    };
    if (data.company?.trim()) input.company = data.company.trim();
    if (data.plan_slug) input.plan_slug = data.plan_slug;

    try {
      const result = await adminCustomerService.importExisting(input);
      setImported(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setSubmitting(false);
    }
  }

  if (imported) {
    return (
      <UniversalPage
        title="Customer imported"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Customers', href: '/admin/customers' },
          { label: 'Import existing' },
        ]}
      >
        <Card interactive={false} className="border-success/30 bg-success/5 p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" />
            <div className="flex-1 space-y-2 text-sm">
              <p className="font-semibold text-success">
                {data.email} linked to cPanel account{' '}
                <code className="rounded-sm bg-card px-1 text-xs">{data.cpanel_username}</code>.
              </p>
              <p className="text-foreground-soft">
                {imported.invite_sent
                  ? 'Invite email sent — customer sets their portal password by clicking the link.'
                  : 'Invite skipped per your selection. The user exists but has no password set yet.'}
              </p>
              <p className="text-xs text-muted-foreground">
                cPanel password is unchanged — customer continues to use it as before.
              </p>
            </div>
          </div>
        </Card>
        <div className="flex gap-3">
          <Button onClick={() => { setImported(null); setData(EMPTY); }}>
            Import another
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/customers')}>
            Back to customers
          </Button>
        </div>
      </UniversalPage>
    );
  }

  return (
    <UniversalPage
      title="Import existing cPanel customer"
      description="Onboard a customer whose cPanel account already exists on WHM."
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Customers', href: '/admin/customers' },
        { label: 'Import existing' },
      ]}
    >
      <NotificationCard
        tone="info"
        title="No new cPanel account is created"
        body="The customer's existing cPanel account on WHM is linked to a new portal user. Their cPanel password is unchanged — they set a separate portal password by clicking the invite link in their email."
      />

      <form onSubmit={onSubmit} className="space-y-6">
        <Section title="Customer details">
          <FormField label="Email" htmlFor="email" required>
            <Input
              id="email"
              type="email"
              value={data.email}
              onChange={(e) => setField('email', e.target.value)}
              disabled={submitting}
              autoComplete="email"
              autoFocus
            />
          </FormField>
          <FormField label="Full name" htmlFor="full_name" required>
            <Input
              id="full_name"
              value={data.full_name}
              onChange={(e) => setField('full_name', e.target.value)}
              disabled={submitting}
            />
          </FormField>
          <FormField label="Company" htmlFor="company">
            <Input
              id="company"
              value={data.company ?? ''}
              onChange={(e) => setField('company', e.target.value)}
              disabled={submitting}
            />
          </FormField>
        </Section>

        <Section
          title="cPanel account (existing on WHM)"
          description="Pulled from rs61.cphost.co.za — confirm the username and primary domain match."
        >
          <FormField
            label="cPanel username"
            htmlFor="cpanel"
            required
            helper="Exact value from WHM (e.g. sipho47, mkldistr)."
          >
            <Input
              id="cpanel"
              value={data.cpanel_username}
              onChange={(e) => setField('cpanel_username', e.target.value.toLowerCase())}
              disabled={submitting}
              maxLength={16}
            />
          </FormField>
          <FormField label="Primary domain" htmlFor="primary_domain" required>
            <Input
              id="primary_domain"
              value={data.primary_domain}
              onChange={(e) => setField('primary_domain', e.target.value.toLowerCase())}
              disabled={submitting}
              placeholder="hindsight.africa"
            />
          </FormField>
          <FormField label="WHM server" htmlFor="whm_server">
            <Input
              id="whm_server"
              value={data.whm_server ?? ''}
              onChange={(e) => setField('whm_server', e.target.value)}
              disabled={submitting}
            />
          </FormField>
          <FormField label="Plan" htmlFor="plan" helper="Optional. Set later if not known.">
            <select
              id="plan"
              value={data.plan_slug ?? ''}
              onChange={(e) => setField('plan_slug', e.target.value)}
              disabled={submitting}
              className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {PLAN_OPTIONS.map((p) => (
                <option key={p.slug || 'none'} value={p.slug}>
                  {p.label}
                </option>
              ))}
            </select>
          </FormField>
        </Section>

        <Section title="Invite">
          <label className="flex items-center gap-3 text-sm text-ink">
            <Checkbox
              checked={data.send_invite ?? true}
              onCheckedChange={(v) => setField('send_invite', v === true)}
              disabled={submitting}
            />
            Send invite email now (customer sets their portal password via the link)
          </label>
        </Section>

        {error && <NotificationCard tone="danger" title="Import failed" body={error} />}

        <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-border bg-card px-6 py-3">
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={() => navigate('/admin/customers')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} loading={submitting}>
            {submitting ? 'Importing…' : 'Import customer'}
          </Button>
        </div>
      </form>
    </UniversalPage>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Card interactive={false}>
      <header className="border-b border-border px-6 py-3">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      </header>
      <div className="space-y-4 px-6 py-5">{children}</div>
    </Card>
  );
}
